import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { patch } from '../api/api';
import { showToast } from '../components/utils/toast';
import { INote } from '../interface/notes';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { useAnalytics } from './useAnalytics';
import { isVersionConflictError, VERSION_CONFLICT_CONFIG } from '../helpers/versionConflictHelper';

const updateNote = async (
  noteId: string,
  request: INote,
  params: Record<string, string>,
): Promise<INote> => {
  return patch(`${API_URLS.NOTES_LIST}/${noteId}`, request, { params, ...VERSION_CONFLICT_CONFIG });
};

export const useUpdateNote = (): UseMutationResult<
  INote,
  AxiosError<{ message?: string; type?: string }>,
  { noteId: string; request: INote; params: Record<string, string> }
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: ({ noteId, request, params }) => updateNote(noteId, request, params),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.NOTES_UPDATED);
      showToast('success', t('note:noteEditSuccess'));
    },
    onError: (error) => {
      if (isVersionConflictError(error)) return;
      showToast('error', error.message);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
